import * as React from "react"
import {
  useLogsForBuildQuery,
  useBuildLogCreatedSubscription,
  LogsForBuildDocument,
  LogsForBuildQueryVariables,
  LogsForBuildQuery,
} from "@modules/build/logs/raw/queries.generated"
import { NetworkStatus } from "apollo-client"

// This is the limit used on the backend
const RAW_LOGS_LIMIT_PER_PAGE = 500

export function useBuildRawLogs(buildId: string, siteId: string) {
  const hasLoadedAll = React.useRef<boolean>(false)

  const {
    data,
    loading,
    error,
    fetchMore,
    networkStatus,
  } = useLogsForBuildQuery({
    variables: {
      buildId,
      siteId,
      offset: 0,
    },
    fetchPolicy: `cache-and-network`,
  })

  const loadMoreLogs = () => {
    const currentLogsSize = data?.logsForBuild?.length ?? 0
    const hasMoreLogs =
      currentLogsSize >= RAW_LOGS_LIMIT_PER_PAGE && !hasLoadedAll.current
    const isAlreadyFetching = networkStatus === NetworkStatus.fetchMore

    if (!hasMoreLogs || isAlreadyFetching) {
      return
    }

    return fetchMore({
      variables: {
        buildId,
        siteId,
        offset: currentLogsSize,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const moreLogs = fetchMoreResult?.logsForBuild ?? []

        if (moreLogs.length === 0) {
          return previousResult
        }

        return {
          ...previousResult,
          logsForBuild: [...(previousResult?.logsForBuild ?? []), ...moreLogs],
        }
      },
    }).then(({ data }) => {
      const moreLogsSize = data?.logsForBuild?.length ?? 0
      // When the number of returned logs is 500, it means that there _might_ be more logs available
      // Otherwise the number of returned logs is all what is left and everything is loaded
      //
      // Example: if there are 1031 logs total, we will receive those logs in three batches: 500, 500, 31
      // The last batch is lesser than 500, so there couldn't be any more logs to fetch
      hasLoadedAll.current = moreLogsSize < RAW_LOGS_LIMIT_PER_PAGE
    })
  }

  useBuildLogCreatedSubscription({
    variables: {
      buildId,
    },
    onSubscriptionData: ({ client, subscriptionData }) => {
      const queryInfo = {
        query: LogsForBuildDocument,
        variables: {
          buildId,
          siteId,
        },
      }

      try {
        const currentLogs = client.readQuery<
          LogsForBuildQuery,
          LogsForBuildQueryVariables
        >(queryInfo)

        if (!currentLogs) {
          return
        }

        const currentLogsForBuild = currentLogs?.logsForBuild || []
        const logsPayload = subscriptionData?.data?.buildLogCreated || []

        currentLogs.logsForBuild = [...currentLogsForBuild, ...logsPayload]

        client.writeQuery<LogsForBuildQuery, LogsForBuildQueryVariables>({
          ...queryInfo,
          data: currentLogs,
        })
      } catch (e) {
        console.error(e)
      }
    },
  })

  const buildRawLogs = data?.logsForBuild || []

  return [
    buildRawLogs,
    { error, loading, loadMoreLogs, networkStatus },
  ] as const
}
